import React, { useEffect, useState } from "react";
import PageLayout from "../components/PageLayout";
import { useTheme } from "../DataContext";
import { getTheme, getThemeNames } from "../theme";
import axios from "axios";

export default function Settings() {
  const { theme, themeName, setTheme } = useTheme();
  const [msUrl, setMsUrl]     = useState("");
  const [msMode, setMsMode]   = useState("demo");
  const [saved, setSaved]     = useState(false);
  const [planFiles, setPlanFiles] = useState([]);
  const [xmlFile, setXmlFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractResult, setExtractResult] = useState(null);
  const [extractError, setExtractError] = useState("");

  const themes = getThemeNames();

  useEffect(() => {
    refreshPlanFiles();
  }, []);

  const refreshPlanFiles = async () => {
    try {
      const response = await axios.get("/api/ms-project/available-files");
      setPlanFiles(response.data.files || []);
    } catch {
      setPlanFiles([]);
    }
  };

  const uploadXml = async () => {
    if (!xmlFile) return;
    setExtracting(true);
    setExtractError("");
    setExtractResult(null);
    try {
      const form = new FormData();
      form.append("file", xmlFile);
      const response = await axios.post("/api/ms-project/upload-xml", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setExtractResult(response.data);
      await refreshPlanFiles();
    } catch (error) {
      setExtractError(error.response?.data?.detail || error.message || "XML extraction failed");
    } finally {
      setExtracting(false);
    }
  };

  const extractLocalXml = async (name) => {
    setExtracting(true);
    setExtractError("");
    setExtractResult(null);
    try {
      const response = await axios.post(`/api/ms-project/extract-local-xml/${encodeURIComponent(name)}`);
      setExtractResult(response.data);
    } catch (error) {
      setExtractError(error.response?.data?.detail || error.message || "Local XML extraction failed");
    } finally {
      setExtracting(false);
    }
  };

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <PageLayout title="Platform Settings" subtitle="Theme, data connections, and platform configuration">
      {/* Theme Selection */}
      <div className="pmo-card" style={{ marginBottom: "20px" }}>
        <div className="section-title">UI Theme</div>
        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
          {themes.map(t => {
            const th = getTheme(t.key);
            const active = t.key === themeName;
            const borderColor = active ? th.accent : (th.bg === "#FFFFFF" || th.bg === "#EBD2B4" ? "#999" : "#333");
            return (
              <div key={t.key} onClick={() => setTheme(t.key)} style={{
                width: "120px", height: "80px", borderRadius: "10px",
                background: th.bg,
                border: `2px solid ${borderColor}`,
                cursor: "pointer", display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center", gap: "4px",
                transition: "transform 0.15s",
                transform: active ? "scale(1.05)" : "scale(1)",
                boxShadow: active ? `0 0 12px ${th.accent}44` : "none",
              }}>
                <div style={{ fontSize: "20px", color: th.accent }}>✦</div>
                <div style={{ fontSize: "10px", color: th.accent, fontWeight: 600, textAlign: "center", lineHeight: 1.2, padding: "0 4px" }}>{t.name}</div>
                {active && <div style={{ fontSize: "8px", color: th.accent, opacity: 0.8 }}>ACTIVE</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Data Source Configuration */}
      <div className="pmo-card" style={{ marginBottom: "20px" }}>
        <div className="section-title">Microsoft Project Server Connection</div>
        <div style={{ display: "flex", gap: "12px", marginBottom: "14px" }}>
          {[
            { k: "demo",  l: "Demo Mode",      d: "Built-in synthetic portfolio data" },
            { k: "odata", l: "OData REST API",  d: "Connect via Project Server REST endpoint" },
            { k: "sql",   l: "SQL Direct",      d: "Direct SQL Server connection (requires backend)" },
          ].map(m => (
            <div key={m.k} onClick={() => setMsMode(m.k)} style={{
              flex: 1, padding: "12px 16px", borderRadius: "8px", cursor: "pointer",
              border: `1px solid ${msMode === m.k ? theme.accent : theme.cardBorder}`,
              background: msMode === m.k ? `${theme.accent}12` : "transparent",
            }}>
              <div style={{ fontSize: "12px", fontWeight: 600, color: msMode === m.k ? theme.accent : theme.textSecondary }}>{m.l}</div>
              <div style={{ fontSize: "10px", color: theme.textMuted, marginTop: "3px" }}>{m.d}</div>
            </div>
          ))}
        </div>

        {msMode !== "demo" && (
          <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: "block", fontSize: "10px", color: theme.textMuted, marginBottom: "5px", textTransform: "uppercase" }}>
                {msMode === "odata" ? "Project Server URL" : "SQL Connection String"}
              </label>
              <input type="text" value={msUrl} onChange={e => setMsUrl(e.target.value)}
                placeholder={msMode === "odata" ? "https://pwa.company.com/pwa/_api/..." : "Server=sql;Database=ProjectWebApp;..."}
                style={{ width: "100%" }} />
            </div>
            <div style={{ alignSelf: "flex-end" }}>
              <button className="btn-primary" style={{ padding: "9px 16px" }}>TEST CONNECTION</button>
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: "block", fontSize: "10px", color: theme.textMuted, marginBottom: "5px", textTransform: "uppercase" }}>Data Refresh Interval</label>
            <select style={{ width: "100%" }}>
              <option value="realtime">Real-time (live queries)</option>
              <option value="hourly">Hourly</option>
              <option value="daily">Daily</option>
              <option value="manual">Manual refresh only</option>
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: "block", fontSize: "10px", color: theme.textMuted, marginBottom: "5px", textTransform: "uppercase" }}>ML Model Retrain</label>
            <select style={{ width: "100%" }}>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="manual">Manual only</option>
            </select>
          </div>
        </div>
      </div>

      {/* MS Project XML Extraction */}
      <div className="pmo-card" style={{ marginBottom: "20px" }}>
        <div className="section-title">Microsoft Project XML Dataset Extraction</div>
        <div style={{ fontSize: "12px", color: theme.textSecondary, lineHeight: 1.5, marginBottom: "14px" }}>
          Import a Microsoft Project XML export to create a complete PMO database: normalized tasks, resources,
          assignments, calendars, risk predictions, and an exhaustive XML key/value catalog for future intelligence.
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "10px", alignItems: "end", marginBottom: "14px" }}>
          <div>
            <label style={{ display: "block", fontSize: "10px", color: theme.textMuted, marginBottom: "5px", textTransform: "uppercase" }}>
              Upload MS Project XML
            </label>
            <input
              type="file"
              accept=".xml,text/xml,application/xml"
              onChange={event => setXmlFile(event.target.files?.[0] || null)}
              style={{ width: "100%" }}
            />
          </div>
          <button onClick={uploadXml} disabled={!xmlFile || extracting} className="btn-primary">
            {extracting ? "EXTRACTING..." : "EXTRACT XML"}
          </button>
        </div>

        <div style={{ marginBottom: "12px" }}>
          <div style={{ fontSize: "10px", color: theme.textMuted, marginBottom: "6px", textTransform: "uppercase" }}>
            Files in msproject_plan_files
          </div>
          <div style={{ display: "grid", gap: "8px" }}>
            {planFiles.length === 0 && (
              <div style={{ fontSize: "12px", color: theme.textMuted }}>No plan files detected by the backend yet.</div>
            )}
            {planFiles.map(file => (
              <div key={file.name} style={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gap: "10px",
                alignItems: "center",
                padding: "10px 12px",
                border: `1px solid ${theme.cardBorder}`,
                borderRadius: "8px",
                background: `${theme.cardElevated || theme.card}88`,
              }}>
                <div>
                  <div style={{ fontSize: "12px", color: theme.textSecondary, fontWeight: 600 }}>{file.name}</div>
                  <div style={{ fontSize: "10px", color: theme.textMuted }}>
                    {(file.size / 1024).toFixed(1)} KB · {file.parse_ready ? "XML ready" : "Export this file as XML first"}
                  </div>
                </div>
                <button
                  onClick={() => extractLocalXml(file.name)}
                  disabled={!file.parse_ready || extracting}
                  className={file.parse_ready ? "btn-primary" : "btn-secondary"}
                  style={{ padding: "8px 12px" }}
                >
                  {file.parse_ready ? "EXTRACT" : "MPP"}
                </button>
              </div>
            ))}
          </div>
        </div>

        {extractError && (
          <div style={{ padding: "10px 12px", border: `1px solid ${theme.danger}`, color: theme.danger, borderRadius: "8px", marginBottom: "12px", fontSize: "12px" }}>
            {extractError}
          </div>
        )}

        {extractResult && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "10px" }}>
            {[
              ["Tasks", extractResult.summary.task_count],
              ["Resources", extractResult.summary.resource_count],
              ["Assignments", extractResult.summary.assignment_count],
              ["XML Field Paths", extractResult.summary.xml_field_path_count],
              ["Valued Paths", extractResult.summary.valued_field_path_count],
              ["Critical Tasks", extractResult.summary.critical_task_count],
              ["High Risk Tasks", extractResult.summary.high_risk_task_count],
              ["Avg Complete", `${extractResult.summary.avg_percent_complete ?? 0}%`],
            ].map(([label, value]) => (
              <div key={label} style={{ padding: "10px", border: `1px solid ${theme.cardBorder}`, borderRadius: "8px" }}>
                <div style={{ fontSize: "10px", color: theme.textMuted, textTransform: "uppercase" }}>{label}</div>
                <div style={{ fontSize: "18px", color: theme.accent, fontWeight: 700 }}>{value}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI Configuration */}
      <div className="pmo-card" style={{ marginBottom: "20px" }}>
        <div className="section-title">AI Agent Configuration</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
          <div>
            <label style={{ display: "block", fontSize: "10px", color: theme.textMuted, marginBottom: "5px", textTransform: "uppercase" }}>Default Model</label>
            <select style={{ width: "100%" }}>
              <option value="claude-sonnet-4-6">Claude Sonnet 4.6 (Recommended)</option>
              <option value="claude-opus-4-7">Claude Opus 4.7 (Advanced)</option>
              <option value="claude-haiku-4-5-20251001">Claude Haiku 4.5 (Fast)</option>
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: "10px", color: theme.textMuted, marginBottom: "5px", textTransform: "uppercase" }}>Report Language</label>
            <select style={{ width: "100%" }}>
              <option value="en">English</option>
              <option value="ar">Arabic</option>
              <option value="fr">French</option>
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: "10px", color: theme.textMuted, marginBottom: "5px", textTransform: "uppercase" }}>Anthropic API Key</label>
            <input type="password" placeholder="sk-ant-api03-..." defaultValue={localStorage.getItem("pmo_api_key") || ""} style={{ width: "100%" }}
              onChange={e => localStorage.setItem("pmo_api_key", e.target.value)} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: "10px", color: theme.textMuted, marginBottom: "5px", textTransform: "uppercase" }}>Failure Probability Alert Threshold</label>
            <input type="number" defaultValue={50} min={1} max={99} style={{ width: "100%" }} />
          </div>
        </div>
      </div>

      {/* Platform Info */}
      <div className="pmo-card" style={{ marginBottom: "20px" }}>
        <div className="section-title">Platform Information</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
          {[
            ["Platform",     "Shuriken PMO Intelligent Platform"],
            ["Short Name",   "Shuriken PMO-P"],
            ["Version",      "v1.0.0"],
            ["Build Date",   "2026-05-11"],
            ["ML Engine",    "Random Forest + XGBoost + LightGBM"],
            ["Target Accuracy", "≥ 98%"],
            ["Author",       "Hassaan Alrifai"],
            ["Organisation", "Cyber Shuriken Technologies"],
            ["Frontend",     "React 18 + Recharts"],
            ["Backend",      "FastAPI + Python 3.12"],
            ["Deployment",   "Standalone + cybershuriken.com/platforms/pmo-ip"],
          ].map(([l, v]) => (
            <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${theme.cardBorder}22` }}>
              <span style={{ fontSize: "11px", color: theme.textMuted }}>{l}</span>
              <span style={{ fontSize: "11px", color: theme.textSecondary, fontWeight: 500 }}>{v}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", gap: "10px" }}>
        <button onClick={handleSave} className="btn-primary">{saved ? "✓ SAVED" : "SAVE SETTINGS"}</button>
        <button className="btn-secondary">RESET DEFAULTS</button>
      </div>
    </PageLayout>
  );
}
